import { 
  Bed, 
  Bath, 
  Car, 
  Maximize2, 
  Home,
  DollarSign,
  Calendar,
  MapPin,
  Check
} from 'lucide-react';
import { formatCurrency, formatArea } from '@/lib/format';
import { PROPERTY_TYPES, PROPERTY_PURPOSES } from '@/types/property';
import type { Property } from '@/types/property';

interface PropertyFeaturesProps {
  property: Property;
}

export function PropertyFeatures({ property }: PropertyFeaturesProps) {
  const mainFeatures = [
    { icon: Maximize2, label: 'Área Total', value: property.total_area ? formatArea(property.total_area) : null },
    { icon: Home, label: 'Área Construída', value: property.built_area ? formatArea(property.built_area) : null },
    { icon: Bed, label: 'Quartos', value: property.bedrooms ? `${property.bedrooms} quartos` : null },
    { icon: Bed, label: 'Suítes', value: property.suites ? `${property.suites} suítes` : null },
    { icon: Bath, label: 'Banheiros', value: property.bathrooms ? `${property.bathrooms} banheiros` : null },
    { icon: Car, label: 'Vagas', value: property.parking_spots ? `${property.parking_spots} vagas` : null },
  ].filter(f => f.value);

  const financialInfo = [
    { label: 'Preço', value: formatCurrency(property.price), highlight: true },
    { label: 'Condomínio', value: property.condo_fee ? formatCurrency(property.condo_fee) + '/mês' : null },
    { label: 'IPTU', value: property.iptu ? formatCurrency(property.iptu) + '/ano' : null },
  ].filter(f => f.value);

  const propertyInfo = [
    { label: 'Tipo', value: PROPERTY_TYPES[property.type] },
    { label: 'Finalidade', value: PROPERTY_PURPOSES[property.purpose] },
    { label: 'Cidade', value: property.city },
    { label: 'Bairro', value: property.neighborhood },
    { label: 'Endereço', value: property.street ? `${property.street}${property.number ? `, ${property.number}` : ''}` : null },
  ].filter(f => f.value);

  return (
    <div className="space-y-8">
      {/* Main Features */}
      <div>
        <h3 className="mb-4 text-lg font-bold text-foreground">Características</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {mainFeatures.map((feature, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-4"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <feature.icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{feature.label}</p>
                <p className="font-semibold text-foreground">{feature.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Financial Info */}
      <div>
        <h3 className="mb-4 text-lg font-bold text-foreground">Valores</h3>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="space-y-3">
            {financialInfo.map((info, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between ${
                  info.highlight ? 'text-lg font-bold' : ''
                }`}
              >
                <span className="text-muted-foreground">{info.label}</span>
                <span className={info.highlight ? 'text-primary' : 'text-foreground'}>
                  {info.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Property Info */}
      <div>
        <h3 className="mb-4 text-lg font-bold text-foreground">Informações do Imóvel</h3>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="space-y-3">
            {propertyInfo.map((info, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-muted-foreground">{info.label}</span>
                <span className="text-foreground">{info.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Features */}
      {property.features && property.features.length > 0 && (
        <div>
          <h3 className="mb-4 text-lg font-bold text-foreground">Comodidades</h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {property.features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-center gap-2 text-sm"
              >
                <Check className="h-4 w-4 text-primary" />
                <span className="text-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
